import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import * as BsIcons from "react-icons/bs";
import { IoPersonCircle } from "react-icons/io5";
import logoIventas from "../../../assets/images/logos/logo_darkblue.svg";

import {
  LinksContainer,
  LinkContainer,
  LinkIcon,
  LinkName,
} from "./links.style";
import {
  NavContainer,
  BrandSection,
  LogoPill,
  HeaderActions,
  HeaderProfile,
} from "./Nav.style";
import links from "../../../const/links";
import CONSTANTS from "../../../const/constants";
import useBotMakerNav from "../../../hooks/useBotMakerNav";
import { navigateTo } from "../../../utils/zoneNavigation";
import SidebarModalComponent from "../sidebarModal/SidebarModal.component";
import SedeBadge from "../sedeBadge/SedeBadge.component";
import { isSedeMode } from "../../../utils/partnerAccess/partnerStorage";
import { isPartnerAccessEnabled } from "../../../utils/partnerAccess/partnerFlag";
import { hasPartnerAccessState, peekPartnerDirectory } from "../../../utils/partnerAccess/partnerDirectory";
import { isSedeSearchShortcut } from "../../../utils/partnerAccess/sedeSearchShortcut";
import { canSeeInicio, receivesHumanTasks } from "../../../views/dashboard/inicio/access";
import InicioNavBadge from "../../../views/dashboard/inicio/components/inicio-nav-badge";

// Compañía de mobieffects iventas - todos los usuarios tienen acceso
const MOBIEFFECTS_COMPANY_ID = "5ed824990274390007afcc70";

// Partner access: the sections a tab working a sede can reach (see the
// allow-list in router/routeGuard.js). The permission filters below still
// apply to them, and so does the boards flag of the company for 'Boards'.
// "Bot Maker" is left out on purpose: it stays hidden in a sede whatever its
// own grant says. 'Inicio' shows only when the session carries the member's
// linked account of the sede (`canSeeInicio`; docs/specs/2026-11-04-inicio-en-multisede).
const SEDE_MODE_NAV_ENTRIES = [
  'Inicio',
  'Chat',
  'Boards',
  'Contactos',
  'Multimedia',
  'Respuestas',
  'Equipo',
  'Campañas',
  'Estadísticas',
  'Canales',
  'Configuración',
  'Ayuda',
];

const Nav = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const [showModal, setShowModal] = useState(false);
  const botMakerVisible = useBotMakerNav();
  // Partner access: a tab working a sede only shows the allowed sections.
  const sedeMode = isSedeMode();

  const [sedeSearchRequest, setSedeSearchRequest] = useState(0);

  // The profile button and the sede badge: each click opens the profile menu,
  // or closes it when it is open.
  const handleModal = () => {
    setShowModal((open) => !open);
  };

  // The menu closed itself (a click outside it, a space chosen, the overview
  // opened): it says so, and the buttons keep telling the truth about it.
  const closeModal = useCallback(() => setShowModal(false), []);
  // What holds both buttons. A click there is not a click outside the menu.
  const actionsRef = useRef(null);

  // Partner access: Ctrl+K, or ⌘K on a Mac, opens the profile menu on its sede
  // search. The shortcut works from anywhere in the app, also while the
  // cursor is in a field. Without the build flag nothing listens, and for
  // a user who is not a partner the key keeps its own meaning: Ctrl+K is the
  // browser's address-bar search, and "delete to the end of the line" in a
  // text field on a Mac (the chat composer included).
  const userId = user?.data?.id;
  useEffect(() => {
    if (!isPartnerAccessEnabled()) return undefined;
    const onKeyDown = (event) => {
      if (!isSedeSearchShortcut(event)) return;
      if (!hasPartnerAccessState() && !peekPartnerDirectory({ userId })) return;
      // The browser has its own Ctrl+K (the address bar search).
      event.preventDefault();
      if (event.repeat) return;
      setShowModal(true);
      setSedeSearchRequest((count) => count + 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [userId]);

  return (
    <NavContainer>
      {/* Brand Section - Left */}
      <BrandSection>
        <LogoPill>
          <img src={logoIventas} alt="iVentas" />
        </LogoPill>
      </BrandSection>

      {/* Navigation - Center */}
      <LinksContainer aria-label="Navegación principal">
        {links
          .filter(
            link => !sedeMode || SEDE_MODE_NAV_ENTRIES.includes(link.name)
          ).filter(
            link => !(user.data.rol.name === 'groupadmin' && link.name === 'Equipo')
          ).filter(
            link => !(user?.data?.company?.isBoardsActive !== true && link.name === 'Boards')
          ).filter(
            // "Bot Maker" solo se muestra si el `/me` nuevo confirma el
            // grant `bot-maker.view` en `navigation` — ver useBotMakerNav.
            link => link.name !== 'Bot Maker' || botMakerVisible
          ).filter(
            link => {
              // Tareas para agentes humanos: misma regla que el guard de /inicio.
              if (link.name === 'Inicio') {
                return canSeeInicio(user.data);
              }

              // Filtro especial para Resumen (/dashboard, AI Summaries)
              if (link.name === 'Resumen') {
                const companyId = user.data?.company?.id;
                const isAiSummariesEnabled = user.data?.company?.isAiSummariesEnabled;
                const roleName = user.data?.rol?.name;
                const isSuperadmin = roleName === CONSTANTS.SUPERADMIN;

                // Para mobieffects iventas: habilitado para todos los usuarios
                if (companyId === MOBIEFFECTS_COMPANY_ID) {
                  return true;
                }

                // Para otras compañías: solo si isAiSummariesEnabled es true Y es superadmin
                if (isAiSummariesEnabled && isSuperadmin) {
                  return true;
                }

                return false;
              }

              // Acceso abierto explícito (otros links legacy)
              if (link.openAccess) {
                return true;
              }

              return true;
            }
          ).map((link) => {
            if (!user?.data?.permissions) return null;

            const hasPermission =
              link.openAccess ||
              link?.permissions?.some(
                (linkPermission) => user.data.permissions[linkPermission]
              );
            if (!hasPermission) return null;

            const IconComponent = link.icon ? BsIcons[link.icon] : null;
            const isActive = link.routes.some(
              (route) =>
                location.pathname === route ||
                location.pathname.startsWith(`${route}/`)
            );
            const showTaskBadge = link.name === 'Inicio' && receivesHumanTasks(user.data);

            return (
              <LinkContainer
                key={link.routes[0]}
                type="button"
                onClick={() => navigateTo(history, link.routes[0])}
                isActive={isActive}
              >
                {IconComponent && (
                  <LinkIcon isActive={isActive} style={showTaskBadge ? { position: 'relative' } : undefined}>
                    <IconComponent />
                    {showTaskBadge && <InicioNavBadge />}
                  </LinkIcon>
                )}
                <LinkName>
                  {link.name === 'Boards' ? 'Tableros' : link.name}
                </LinkName>
              </LinkContainer>
            );
          })}
      </LinksContainer>

      {/* Actions Section - Right */}
      <HeaderActions ref={actionsRef}>
        {/* Both open and close the same profile menu. */}
        <SedeBadge expanded={showModal} onClick={handleModal} />
        <HeaderProfile
          type="button"
          title="Perfil"
          aria-label="Perfil"
          aria-haspopup="dialog"
          aria-expanded={showModal}
          onClick={handleModal}
        >
          <IoPersonCircle aria-hidden="true" />
        </HeaderProfile>
      </HeaderActions>

      <SidebarModalComponent
        show={showModal}
        searchFocusRequest={sedeSearchRequest}
        onClose={closeModal}
        anchorRef={actionsRef}
      />
    </NavContainer>
  );
};

export default Nav;
